import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Image,
  TextInput,
  ActivityIndicator,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import EspacioPatrocinado from './EspacioPatrocinado';
import { useThemeColors } from '../constants/theme';

/**
 * CALIFICAR AL FINAL DEL VIAJE — CINCO ESTRELLAS Y UN COMENTARIO.
 *
 * ═══ QUÉ HABÍA ANTES, Y POR QUÉ NO SERVÍA ═══
 *
 * Tres botones —Excelente / Regular / Malo— y nada más. El fundador lo reportó
 * después del primer viaje real de la app:
 *
 *   "no tenemos la opción de ponerle el comentario al chofer y solo las 3
 *    caritas para seleccionar, y no aparecen las 5 estrellas para darle"
 *
 * Y había algo peor que la falta de estrellas: al tocar una carita, el modal
 * llamaba a `onSubmit` **solo 500 ms después, sin botón de enviar**. Aunque
 * hubiera existido un campo de comentario, no daba tiempo material a escribir
 * en él. Tampoco se podía cambiar de opinión.
 *
 * Las tres caritas se traducían a 5, 3 y 1 estrellas en map.tsx. O sea que la
 * escala de cinco YA existía; lo que faltaba era dejar al pasajero usarla. Un
 * chofer que merece un 4 recibía un 5 o un 3, y esa nota es la que decide su
 * reputación delante del próximo pasajero.
 *
 * ═══ POR QUÉ ESTO NO NECESITA TOCAR EL SERVIDOR ═══
 *
 * Comprobado leyendo `submitRideRating` (functions/src/ratings.ts): la función
 * ya acepta `driverRating` de 1 a 5 (`clampStar`) y un `comment` que sanea y
 * guarda en el viaje y en la colección `/ratings`. Y está medido con datos
 * reales: la calificación del primer viaje quedó escrita como
 * `driverRating: 5` con un campo `comment` al lado, vacío porque la app nunca
 * lo mandó. El camino estaba entero; solo faltaba la pantalla.
 */

/** Lo que se le dice al pasajero según las estrellas que va tocando. */
const TEXTO_POR_ESTRELLA: Record<number, string> = {
  1: 'Muy mal — tuve un problema serio',
  2: 'Mal',
  3: 'Regular — puede mejorar',
  4: 'Bien',
  5: 'Excelente — llegó a tiempo y fue amable',
};

interface DriverRatingModalProps {
  visible: boolean;
  driverName: string;
  driverPhoto: string;
  onClose: () => void;
  /** Las estrellas (1-5) y el comentario, que puede ir vacío. */
  onSubmit: (rating: number, comment: string) => void | Promise<void>;
  /**
   * Texto de la pregunta. Se parametriza porque esta misma pantalla la usa
   * ahora el CHOFER para calificar al pasajero, y ahí no se pregunta por el
   * vehículo ni por la puntualidad del chofer.
   */
  titulo?: string;
  pregunta?: string;
  /** Etiqueta de la persona calificada: 'Chofer' o 'Pasajero'. */
  etiquetaPersona?: string;
}

export const DriverRatingModal = ({
  visible,
  driverName,
  driverPhoto,
  onClose,
  onSubmit,
  titulo = 'Viaje Finalizado',
  pregunta = '¿Qué tal tu viaje con el chofer?',
  etiquetaPersona = 'Chofer',
}: DriverRatingModalProps) => {
  const colors = useThemeColors();
  const [estrellas, setEstrellas] = useState(0);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Cada viaje empieza con la hoja en blanco. Sin esto, al segundo viaje el
  // pasajero se encontraría las estrellas y el comentario del anterior ya
  // puestos y podría enviarlos sin darse cuenta.
  useEffect(() => {
    if (visible) {
      setEstrellas(0);
      setComentario('');
      setEnviando(false);
    }
  }, [visible]);

  const enviar = async () => {
    if (estrellas < 1 || enviando) return;
    setEnviando(true);
    try {
      await onSubmit(estrellas, comentario.trim());
    } finally {
      // Quien llama cierra el modal; si algo falla, el botón vuelve a estar
      // vivo en vez de quedarse girando para siempre.
      setEnviando(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      // H37 (auditoría 16-sep-2026) — Sin esto, en Android un Modal visible se
      // traga el botón Atrás y no pasa NADA: la app parece congelada, y es lo
      // primero que prueba un revisor de Play. Atrás hace lo mismo que "Omitir
      // por ahora", y con la misma condición: mientras se envía la calificación
      // ese botón está deshabilitado, así que Atrás tampoco cierra.
      onRequestClose={() => {
        if (!enviando) onClose();
      }}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        // El comentario es un campo de texto en la mitad de abajo de la
        // pantalla: sin esto, el teclado lo tapa y el pasajero escribe a ciegas.
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.overlay}>
          <View style={[styles.container, { backgroundColor: colors.card }]}>
            <ScrollView
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ alignItems: 'center' }}
            >
              {/* Quién es la persona que se califica */}
              <View style={styles.header}>
                {driverPhoto && driverPhoto.startsWith('http') ? (
                  <Image source={{ uri: driverPhoto }} style={styles.driverPhoto} />
                ) : (
                  <View
                    style={[
                      styles.driverPhoto,
                      { backgroundColor: '#E1E8ED', justifyContent: 'center', alignItems: 'center' },
                    ]}
                  >
                    <Ionicons name="person" size={40} color="#829AB1" />
                  </View>
                )}
                <Text style={[styles.title, { color: colors.text }]}>{titulo}</Text>
                <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                  {etiquetaPersona}: {driverName}
                </Text>
              </View>

              <Text style={[styles.questionText, { color: colors.text }]}>{pregunta}</Text>

              {/* ── LAS CINCO ESTRELLAS ─────────────────────────────────────
                  Grandes a propósito: se tocan con el pulgar, de pie en la
                  acera y a veces de noche. 44 px es el mínimo cómodo. */}
              <View style={styles.estrellas}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <TouchableOpacity
                    key={n}
                    onPress={() => setEstrellas(n)}
                    accessibilityRole="button"
                    accessibilityLabel={`${n} ${n === 1 ? 'estrella' : 'estrellas'}`}
                    accessibilityState={{ selected: estrellas === n }}
                    hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
                    style={styles.estrellaBtn}
                  >
                    <Ionicons
                      name={n <= estrellas ? 'star' : 'star-outline'}
                      size={40}
                      color={n <= estrellas ? '#FFC107' : '#C9D2DD'}
                    />
                  </TouchableOpacity>
                ))}
              </View>

              {/* El significado de lo que acaba de tocar. Mientras no toque
                  nada se le dice qué hacer, en vez de dejar el hueco mudo. */}
              <Text
                style={[
                  styles.textoEstrella,
                  { color: estrellas > 0 ? colors.text : colors.textSecondary },
                ]}
              >
                {estrellas > 0 ? TEXTO_POR_ESTRELLA[estrellas] : 'Toca las estrellas para calificar'}
              </Text>

              {/* ── EL COMENTARIO ───────────────────────────────────────────
                  Opcional de verdad: el botón de enviar funciona sin él. Lo
                  que NO puede pasar es que no exista, que es lo que pasaba. */}
              <Text style={[styles.etiquetaComentario, { color: colors.textSecondary }]}>
                Comentario (opcional)
              </Text>
              <TextInput
                style={[
                  styles.comentario,
                  { color: colors.text, borderColor: colors.border, backgroundColor: colors.background },
                ]}
                value={comentario}
                onChangeText={setComentario}
                placeholder={
                  etiquetaPersona === 'Pasajero'
                    ? 'Cuéntanos cómo fue el pasajero…'
                    : 'Cuéntanos cómo fue el viaje…'
                }
                placeholderTextColor={colors.textSecondary}
                multiline
                numberOfLines={3}
                // El servidor recorta el comentario al guardarlo; el tope aquí
                // es para que el usuario vea el límite antes de escribir de más.
                maxLength={300}
                textAlignVertical="top"
              />
              <Text style={[styles.contador, { color: colors.textSecondary }]}>
                {comentario.length}/300
              </Text>

              {/* ── ENVIAR ──────────────────────────────────────────────────
                  ⚠️ CON BOTÓN, Y ESO ES EL ARREGLO DE FONDO. La versión
                  anterior enviaba sola 500 ms después de tocar una carita: no
                  daba tiempo a escribir un comentario ni a cambiar de opinión.
                  Nada sale hasta que la persona lo decide. */}
              <TouchableOpacity
                style={[
                  styles.enviarBtn,
                  { backgroundColor: estrellas > 0 ? '#0D2C54' : '#C9D2DD' },
                ]}
                onPress={enviar}
                disabled={estrellas < 1 || enviando}
                accessibilityRole="button"
                accessibilityState={{ disabled: estrellas < 1 || enviando }}
              >
                {enviando ? (
                  <ActivityIndicator color="#FFF" />
                ) : (
                  <Text style={styles.enviarTexto}>Enviar calificación</Text>
                )}
              </TouchableOpacity>

              <TouchableOpacity style={styles.closeBtn} onPress={onClose} disabled={enviando}>
                <Text style={{ color: colors.textSecondary, fontWeight: 'bold' }}>
                  Omitir por ahora
                </Text>
              </TouchableOpacity>

              {/* ══ EL ESPACIO PATROCINADO, AL FINAL ══════════════════════════
                  Debajo del botón de enviar y del de omitir: calificar al
                  chofer es lo que esta pantalla vino a hacer, y un anuncio no
                  puede empujarlo ni competir con él. Quien ya calificó o ya
                  omitió está de salida, y ese es el momento de enseñarlo.
                  Al chofer no le sale: el componente devuelve null sin rol de
                  pasajero. */}
              <EspacioPatrocinado hueco="recibo" />
            </ScrollView>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  container: {
    borderRadius: 24,
    padding: 20,
    // Con el comentario y el teclado abiertos la tarjeta puede pasarse de
    // alto; el ScrollView de dentro la deja recorrer en vez de recortar el
    // botón de enviar, que es justo lo que no puede quedar fuera.
    maxHeight: '88%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
  },
  header: {
    alignItems: 'center',
    marginBottom: 14,
    marginTop: -50, // la foto flota por encima del borde
  },
  driverPhoto: {
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 4,
    borderColor: '#FFF',
    marginBottom: 10,
  },
  title: {
    fontSize: 22,
    fontWeight: '900',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 4,
    textAlign: 'center',
  },
  questionText: {
    fontSize: 17,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
  },
  estrellas: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 4,
    marginBottom: 6,
  },
  estrellaBtn: {
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  textoEstrella: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
    minHeight: 20,
  },
  etiquetaComentario: {
    alignSelf: 'flex-start',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  comentario: {
    width: '100%',
    minHeight: 76,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 10,
    fontSize: 15,
  },
  contador: {
    alignSelf: 'flex-end',
    fontSize: 11,
    marginTop: 4,
  },
  enviarBtn: {
    width: '100%',
    borderRadius: 14,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    minHeight: 50,
  },
  enviarTexto: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '800',
  },
  closeBtn: {
    marginTop: 14,
    padding: 10,
  },
});
